import { BaseApiService } from './base-api.service';

// Product types
export interface CaProductTranslation {
    [language: string]: {
        id: string;
        name: string;
    };
}

export interface CaProduct {
    eventType: string;
    productSku: string;
    taxCategory: string;
    paymentMethods: CaPaymentMethod[];
    numberOfPayments: number;
    adminFee: number;
    translations: CaProductTranslation;
}

export interface CaProductsResult {
    errors?: string[];
    products: CaProduct[];
}

// Tax category types
export interface CaTaxCategory {
    code: string;
    name: string;
}

export interface CaTaxCategoriesResult {
    errors?: string[];
    taxCategories: CaTaxCategory[];
}

// Event type types
export interface CaEventType {
    code: string;
    name: string;
}

export interface CaEventTypesResult {
    errors?: string[];
    eventTypes: CaEventType[];
}

export enum MfiEventType {
    tmCourse = 'tm-course',
    tmWordOfWisdom = 'tm-word-of-wisdom',
    tmAdvancedTechnique = 'tm-advanced-technique',
    tmSidhisCourse = 'tm-sidhis-course',
    tmSidhisFlyingBlock = 'tm-sidhis-flying-block',
    retreat = 'retreat',
    treatment = 'treatment', // Not supported by MFI Dashboard
    none = 'none', //Teacher Dashboard doesn't know about the product
    undefined = 'undefined',
}

export enum CaPaymentMethod {
    creditCard = 'credit-card',
    eTransfer = 'e-transfer',
    account = 'account',
}

// Product create/update types
export interface CreateProductRequest {
    productSku: string;
    taxCategory: string;
    eventType: string;
    paymentMethods?: CaPaymentMethod[];
    numberOfPayments: number;
    adminFee: number;
    translations: {
        en: {
            name: string;
            id?: string;
        };
        fr: {
            name: string;
            id?: string;
        };
    };
}

// Center types
export interface MfiCenter {
    centerId: string;
    slug: string;
    name: {
        en: string;
        fr: string;
    };
    state: string;
}

export interface MfiCentersResult {
    success: boolean;
    data: MfiCenter[];
}

// Invoice types
export interface CreateInvoiceRequest {
    email: string;
    firstName: string;
    lastName: string;
    locale: string;
    taxState: string;
    taxExempt: boolean;
    paymentMethod: CaPaymentMethod;
    sendEmail: boolean;
    items: {
        productSku: string;
        startDate: string;
        numberOfDays: number;
        amount: number;
        quantity: number;
    }[];
    metadata: {
        teacherName: string;
        centerSlugId: string;
        leadSource?: string;
        memo?: string;
        vedicExpert?: string;
    };
}

export interface CreateInvoiceResponse {
    invoiceId: string;
    invoiceNumber: string;
    paymentUrl: string;
    paymentInstructions?: string;
    totalExcludingTax: number;
    totalTaxes: number;
    total: number;
    subscription?: {
        subscriptionId: string;
        numberOfPayments: number;
        totalExcludingTax: number;
        totalTaxes: number;
        total: number;
    };
}

// Invoice status types (for Payments page)
export interface CaInvoiceStatus {
    invoiceId: string;
    invoiceNumber: string;
    status: 'paid' | 'open' | 'void' | 'uncollectible';
    amountDue: number;
    amountPaid: number;
    currency: string;
    dueDate: string | null;
    paidAt: string | null;
    paymentUrl: string | null;
    courseDate: string | null;
    itemDescriptions: string[];
    userEmail: string | null;
    userName: string | null;
    teacherName: string | null;
}

export interface CaInvoicesStatusResult {
    errors?: string[];
    invoices: CaInvoiceStatus[];
}

export type InvoiceSearchType = 'email' | 'center';

// API Service class for server communication
class PaymentsApiService extends BaseApiService {
    // CA Payments - Get all products
    async getProducts(): Promise<CaProductsResult> {
        const response = await this.client.get<CaProductsResult>('/api/ca/payments/v1/products/all');
        return response.data;
    }

    // CA Payments - Get tax categories
    async getTaxCategories(): Promise<string[]> {
        const response = await this.client.get<string[]>('/api/ca/payments/v1/tax-categories');
        return response.data;
    }

    // CA Payments - Get event types
    async getEventTypes(): Promise<string[]> {
        const response = await this.client.get<string[]>('/api/ca/payments/v1/event-types');
        return response.data;
    }

    // CA Payments - Create product
    async createProduct(request: CreateProductRequest): Promise<CaProductsResult> {
        const response = await this.client.post<CaProductsResult>('/api/ca/payments/v1/products', [request], {
            headers: {
                Authorization: `Bearer ${import.meta.env.VITE_SERVER_API_TOKEN}`,
            },
        });
        return response.data;
    }

    // CA Payments - Update product
    async updateProduct(request: CreateProductRequest): Promise<CaProductsResult> {
        const response = await this.client.put<CaProductsResult>(`/api/ca/payments/v1/products`, [request]);
        return response.data;
    }

    // MFI Centers - Get all centers
    async getCenters(): Promise<MfiCentersResult> {
        const response = await this.client.get<MfiCentersResult>('/api/mfi/apis/centers');
        return response.data;
    }

    // CA Payments - Create invoice
    async createInvoice(request: CreateInvoiceRequest): Promise<CreateInvoiceResponse> {
        const response = await this.client.post<CreateInvoiceResponse>('/api/ca/payments/v1/invoices', request);
        return response.data;
    }

    // CA Payments - Search invoices by email or teacher
    async searchInvoices(searchType: InvoiceSearchType, searchValue: string): Promise<CaInvoicesStatusResult> {
        const params = new URLSearchParams();
        params.set(searchType, searchValue);
        const response = await this.client.get<CaInvoicesStatusResult>(
            `/api/ca/payments/v1/invoices/search?${params.toString()}`,
        );
        return response.data;
    }
    // MFI APIs - Get centers with teachers
    async getCentersWithTeachers(): Promise<any> {
        const response = await this.client.get('/api/mfi/apis/centers/teachers');
        return response.data;
    }
}

// Export singleton instance
export const paymentsApi = new PaymentsApiService();
